import { useState, useRef, useEffect, useCallback } from 'react';

interface AddressSuggestion {
  label: string;
  city: string;
  postcode: string;
  context: string;
}

const DEBOUNCE_MS = 300;

const priceTable: Record<string, { base: number; perKm: number }> = {
  'coursier-moto': { base: 12, perKm: 1.2 },
  utilitaire: { base: 19, perKm: 1.8 },
  'livraison-express': { base: 14.9, perKm: 1.5 },
  demenagement: { base: 49.9, perKm: 2.5 },
};

function estimateDistance(from: string, to: string): number {
  const seed = (from.length * 7 + to.length * 13) % 40;
  return Math.max(3, seed + 2);
}

function calculatePrice(service: string, from: string, to: string): number {
  const dist = estimateDistance(from, to);
  const cfg = priceTable[service] ?? priceTable['coursier-moto'];
  const raw = cfg.base + cfg.perKm * dist;
  return Math.round(raw * 100) / 100;
}

export default function HeroSimulator() {
  const [departure, setDeparture] = useState('');
  const [arrival, setArrival] = useState('');
  const [service, setService] = useState('coursier-moto');
  const [depSuggestions, setDepSuggestions] = useState<AddressSuggestion[]>([]);
  const [arrSuggestions, setArrSuggestions] = useState<AddressSuggestion[]>([]);
  const [showDepDropdown, setShowDepDropdown] = useState(false);
  const [showArrDropdown, setShowArrDropdown] = useState(false);
  const [price, setPrice] = useState<number | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const depRef = useRef<HTMLDivElement>(null);
  const arrRef = useRef<HTMLDivElement>(null);
  const depTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const arrTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSuggestions = useCallback(async (query: string): Promise<AddressSuggestion[]> => {
    if (query.length < 3) return [];
    try {
      const res = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=6&autocomplete=1`,
      );
      const data = await res.json();
      if (!data.features) return [];
      return data.features.map((f: any) => ({
        label: f.properties.label ?? '',
        city: f.properties.city ?? '',
        postcode: f.properties.postcode ?? '',
        context: f.properties.context ?? '',
      }));
    } catch (e) {
      console.error('Failed to fetch address suggestions', e);
      return [];
    }
  }, []);

  const handleDepChange = (val: string) => {
    setDeparture(val);
    setPrice(null);
    if (depTimerRef.current) clearTimeout(depTimerRef.current);
    depTimerRef.current = setTimeout(async () => {
      const results = await fetchSuggestions(val);
      setDepSuggestions(results);
      setShowDepDropdown(results.length > 0);
    }, DEBOUNCE_MS);
  };

  const handleArrChange = (val: string) => {
    setArrival(val);
    setPrice(null);
    if (arrTimerRef.current) clearTimeout(arrTimerRef.current);
    arrTimerRef.current = setTimeout(async () => {
      const results = await fetchSuggestions(val);
      setArrSuggestions(results);
      setShowArrDropdown(results.length > 0);
    }, DEBOUNCE_MS);
  };

  const selectDep = (s: AddressSuggestion) => {
    setDeparture(s.label);
    setShowDepDropdown(false);
    setDepSuggestions([]);
  };

  const selectArr = (s: AddressSuggestion) => {
    setArrival(s.label);
    setShowArrDropdown(false);
    setArrSuggestions([]);
  };

  const handleCalculate = () => {
    if (!departure.trim() || !arrival.trim()) return;
    setIsCalculating(true);
    setTimeout(() => {
      const p = calculatePrice(service, departure, arrival);
      setPrice(p);
      setIsCalculating(false);
    }, 600);
  };

  const handleWhatsApp = () => {
    const msg = `Bonjour, je souhaite un devis pour un transport :\n- Départ : ${departure}\n- Arrivée : ${arrival}\n- Service : ${service}\n- Prix estimé : ${price}€`;
    window.open(`https://wa.me/33632410246?text=${encodeURIComponent(msg)}`, '_blank');
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (depRef.current && !depRef.current.contains(e.target as Node)) {
        setShowDepDropdown(false);
      }
      if (arrRef.current && !arrRef.current.contains(e.target as Node)) {
        setShowArrDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="w-full max-w-4xl mx-auto mt-10">
      <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-6 md:p-8 shadow-2xl border border-red-500/30">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <i className="ri-calculator-line text-xl text-white"></i>
          </div>
          <div>
            <h3 className="text-white font-bold text-lg leading-tight">
              Simulateur de prix instantané
            </h3>
            <p className="text-white/70 text-xs">Obtenez une estimation en quelques secondes</p>
          </div>
          <div className="ml-auto hidden sm:flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1.5">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span className="text-white/90 text-xs font-medium whitespace-nowrap">
              À partir de 12€
            </span>
          </div>
        </div>

        {/* Address Fields */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {/* Departure */}
          <div ref={depRef} className="relative">
            <label className="block text-white/90 text-xs font-semibold mb-1.5 ml-1">
              <i className="ri-map-pin-2-fill mr-1 text-green-300"></i>Adresse de départ
            </label>
            <div className="relative">
              <input
                type="text"
                value={departure}
                onChange={(e) => handleDepChange(e.target.value)}
                onFocus={() => depSuggestions.length > 0 && setShowDepDropdown(true)}
                placeholder="Ex: 12 Rue de Rivoli, Paris"
                className="w-full pl-10 pr-4 py-3.5 bg-white rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50 shadow-inner"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-circle-fill text-[8px] text-green-500"></i>
              </div>
            </div>
            {showDepDropdown && depSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden max-h-64 overflow-y-auto">
                {depSuggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => selectDep(s)}
                    className="w-full text-left px-4 py-3 hover:bg-red-50 transition-colors flex items-start gap-3 border-b border-gray-50 last:border-0 cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-map-pin-line text-sm text-red-500"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 font-medium truncate">{s.label}</p>
                      <p className="text-xs text-gray-400 truncate">{s.context}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Arrival */}
          <div ref={arrRef} className="relative">
            <label className="block text-white/90 text-xs font-semibold mb-1.5 ml-1">
              <i className="ri-map-pin-2-fill mr-1 text-red-300"></i>Adresse d'arrivée
            </label>
            <div className="relative">
              <input
                type="text"
                value={arrival}
                onChange={(e) => handleArrChange(e.target.value)}
                onFocus={() => arrSuggestions.length > 0 && setShowArrDropdown(true)}
                placeholder="Ex: 25 Avenue des Champs-Élysées, Paris"
                className="w-full pl-10 pr-4 py-3.5 bg-white rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50 shadow-inner"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-fill text-sm text-red-500"></i>
              </div>
            </div>
            {showArrDropdown && arrSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden max-h-64 overflow-y-auto">
                {arrSuggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => selectArr(s)}
                    className="w-full text-left px-4 py-3 hover:bg-red-50 transition-colors flex items-start gap-3 border-b border-gray-50 last:border-0 cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-map-pin-line text-sm text-red-500"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 font-medium truncate">{s.label}</p>
                      <p className="text-xs text-gray-400 truncate">{s.context}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Service selector + Calculate */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <select
              value={service}
              onChange={(e) => {
                setService(e.target.value);
                setPrice(null);
              }}
              className="w-full px-4 py-3.5 bg-white/15 text-white rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/30 cursor-pointer appearance-none border border-white/20"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='white' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
              }}
            >
              <option value="coursier-moto">🏍️ Coursier moto — dès 12€</option>
              <option value="livraison-express">📦 Livraison express — dès 14,90€</option>
              <option value="utilitaire">🚐 Utilitaire — dès 19€</option>
              <option value="demenagement">🚚 Déménagement — dès 49,90€</option>
            </select>
          </div>
          <button
            onClick={handleCalculate}
            disabled={!departure.trim() || !arrival.trim() || isCalculating}
            className="sm:w-auto bg-white text-red-600 px-8 py-3.5 rounded-xl font-bold text-sm hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
          >
            {isCalculating ? (
              <>
                <i className="ri-loader-4-line text-lg animate-spin"></i>
                Calcul...
              </>
            ) : (
              <>
                <i className="ri-search-line text-lg"></i>
                Calculer le prix
              </>
            )}
          </button>
        </div>

        {/* Price Result */}
        {price !== null && (
          <div className="mt-5 bg-white rounded-xl p-5 shadow-inner animate-fadeIn">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 text-center sm:text-left">
                <p className="text-xs text-gray-500 font-medium mb-1">Votre estimation de prix</p>
                <div className="flex items-baseline gap-2 justify-center sm:justify-start">
                  <span className="text-4xl font-black text-red-600">{price.toFixed(2)}€</span>
                  <span className="text-sm text-gray-400 font-medium">TTC</span>
                </div>
                <div className="flex items-center gap-4 mt-2 justify-center sm:justify-start">
                  <span className="inline-flex items-center gap-1 text-xs text-green-600 font-semibold">
                    <i className="ri-shield-check-fill text-sm"></i>
                    Prix garanti
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    <i className="ri-time-line text-sm"></i>
                    Livraison rapide
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleWhatsApp}
                  className="flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-5 py-3 rounded-xl font-bold text-sm hover:from-red-600 hover:to-red-700 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-base"></i>
                  Réserver
                </button>
                <a
                  href="tel:+33632410246"
                  className="flex items-center gap-2 border-2 border-red-500 text-red-600 px-5 py-3 rounded-xl font-bold text-sm hover:bg-red-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-line text-base"></i>
                  Appeler
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Bottom trust badges */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <span className="flex items-center gap-1.5 text-white/70 text-xs">
            <i className="ri-shield-check-line text-sm"></i>
            Paiement sécurisé
          </span>
          <span className="flex items-center gap-1.5 text-white/70 text-xs">
            <i className="ri-24-hours-line text-sm"></i>
            Disponible 24h/24
          </span>
          <span className="flex items-center gap-1.5 text-white/70 text-xs">
            <i className="ri-star-fill text-sm text-yellow-400"></i>
            4.9/5 — 500+ avis
          </span>
        </div>
      </div>
    </div>
  );
}
